import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  InputAdornment,
  Link,
  useTheme,
} from '@mui/material';
import {
  Close,
  Search,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { helpContent, HelpEntry } from './helpContent';

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * A slide-out help panel with searchable help topics.
 * Renders all entries from helpContent, filterable by search.
 *
 * Usage:
 *   const [helpOpen, setHelpOpen] = useState(false);
 *   <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
 */
const HelpDrawer: React.FC<HelpDrawerProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const entries = useMemo(() => {
    const all = Object.entries(helpContent) as [string, HelpEntry][];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(
      ([, entry]) =>
        entry.title.toLowerCase().includes(q) ||
        entry.summary.toLowerCase().includes(q) ||
        entry.detail.toLowerCase().includes(q)
    );
  }, [search]);

  const handleToggle = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      sx={{ zIndex: theme.zIndex.modal }}
      PaperProps={{
        sx: {
          width: 380,
          maxWidth: '90vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          pb: 1,
        }}
      >
        <Typography variant="h6">Help</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search help topics..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <List sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1 }}>
        {entries.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No matching help topics found.
            </Typography>
          </Box>
        )}
        {entries.map(([key, entry]) => {
          const isExpanded = expandedKey === key;
          return (
            <React.Fragment key={key}>
              <ListItemButton onClick={() => handleToggle(key)} sx={{ borderRadius: 1 }}>
                <ListItemText
                  primary={entry.title}
                  secondary={entry.summary}
                  primaryTypographyProps={{ variant: 'subtitle2' }}
                  secondaryTypographyProps={{
                    variant: 'body2',
                    noWrap: !isExpanded,
                  }}
                />
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {entry.detail}
                  </Typography>
                  {entry.learnMoreUrl && (
                    <Link
                      href={entry.learnMoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                    >
                      Read full guide
                    </Link>
                  )}
                </Box>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>

      <Box
        component="footer"
        sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.25,
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            fontSize: '0.7rem',
            lineHeight: 1.45,
          }}
        >
          Need further help? Please reach out to us at{' '}
          <Link href="mailto:support@aventior.com" color="inherit" sx={{ fontWeight: 500 }}>
            support@aventior.com
          </Link>
        </Typography>
      </Box>
    </Drawer>
  );
};

export default HelpDrawer;